import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { CFDefaultValueType } from '../../bf-hooks';
import { IBFInsuredObjectItem, IBFProductVariable, IDeviceItem, ITariffDataStep } from 'Services/widgets/interfaces';
import { ICFFactorType, ICFFactorTypeOption } from '../../factor-service';
import { InsuredObjectTypes, ProductFieldTypeEntity } from 'Services/widgets/enums';
import { TooltipPopup } from '../../base-ui-elements/TooltipPopup';
import { FamilyTypes } from 'Services/widgets/enums';
import { Controller, useFormContext } from 'react-hook-form';
import { Form, FormControl, FormGroup } from 'react-bootstrap';
import { blankLink } from 'config';
import { JsonValue } from '@cover42/protobuf-util';
import { FieldType } from '../../base-ui-elements/FactorElement';

export interface AddOnFamilyPanelProps {
  nameField: string;
  insuredObjects: IBFInsuredObjectItem[];
  deviceItems: IDeviceItem[];
  formData: ITariffDataStep;
  factors: ICFFactorType[];
  insuredId: number;
  onChangeHandler: ( isShowFields: boolean, insuredId: number ) => void;
}

export const AddOnFamilyPanel: React.FC<AddOnFamilyPanelProps> = ( {
  nameField, insuredObjects, deviceItems, formData, factors, insuredId, onChangeHandler,
} ) => {
  const { t } = useTranslation( [ 'widgets', 'base' ] );
  const { errors, control, setError, setValue, clearErrors } = useFormContext();

  const [ showTooltip, setShowTooltip ] = React.useState<boolean>( false );
  const [ bfTooltip, setBfTooltip ] = React.useState<string>( '' );
  const [ variable, setVariable ] = React.useState<IBFProductVariable | undefined>( undefined );

  const defaultGroup = insuredObjects.find( ( group ) => group.name === InsuredObjectTypes.Default );

  const factorValues = React.useMemo<ICFFactorTypeOption[]>( () => {
    if ( !variable ) {
      return [];
    }
    const factor = factors.find( ( f ) => f.id === variable.typeId )!;
    if ( factor && factor.values ) {
      return [ ...factor.values ].sort( ( a, b ) => {
        const aKey = parseInt( a.key as string );
        const bKey = parseInt( b.key as string );
        return aKey - bKey;
      } );
    }
    return [];

  }, [ factors, variable ] );

  const getFieldValue = React.useCallback( ( field: IBFProductVariable ): CFDefaultValueType => {
    if ( formData !== null ) {
      const factorNameField = `${field.name}_${field.insuredObjectId}`;
      return formData[factorNameField] ? formData[factorNameField] : '';
    }

    return '';
  }, [ formData ] );

  const getDefaultValue = React.useCallback(
    ( items: ICFFactorTypeOption[], typeField: FieldType, keyItem?: JsonValue ): ICFFactorTypeOption | string => {
      const valField = getFieldValue( variable! );
      if ( valField ) {
        return valField;
      }

      if ( items.length && keyItem ) {
        const findValue = items.find( ( i ) => i.key === keyItem )!;
        if ( typeField === 'radio' ) {
          return findValue ? String( findValue.key ) : '';
        }
        return findValue ? findValue : '';
      }

      return '';

    }, [ getFieldValue, variable ] );

  const getFieldNameFamily = (): string | null => {
    if ( !deviceItems ) {
      return null;
    }

    const foundIndex = deviceItems.findIndex( ( device ) => device.insuredId === insuredId );
    if ( deviceItems.length >= 2 && foundIndex > -1 ) {
      return FamilyTypes[`Family${foundIndex}`];
    }

    return null;
  };

  const onShowTooltip = React.useCallback ( ( isShow: boolean, tooltip: string ): void => {
    setShowTooltip( isShow );
    setBfTooltip( tooltip );
  }, [] );

  const showError = React.useCallback ( ( ): void => {
    setError(
      nameField,
      {
        shouldFocus: true,
        message: t( 'bookingFunnel.messagesOfLimitErrorGm' ),
      },
    );
  }, [ nameField, setError, t ] );

  const handleChange = React.useCallback( ( e, fieldName: string ) => {
    clearErrors( nameField );

    const value = e.target.value;

    if ( value === '0' ) {
      showError();
      onChangeHandler( false, insuredId );
    } else {
      onChangeHandler( true, insuredId );
    }

    setValue( fieldName, value, { shouldValidate: true } );
  }, [ clearErrors, insuredId, nameField, onChangeHandler, showError, setValue ] );

  if ( defaultGroup && variable === undefined ) {
    const fieldNameFamily = getFieldNameFamily();

    if ( fieldNameFamily ) {
      const foundCurrentVariable = defaultGroup.productFields?.find( ( field ) => field.name === fieldNameFamily );

      if ( foundCurrentVariable ) {
        const fieldValue = getFieldValue( foundCurrentVariable );
        if ( fieldValue && fieldValue === '0' ) {
          showError();
        }
        setVariable( foundCurrentVariable );
      }
    }
  }

  return (
    <Fragment>
      { variable && variable.typeEntity === ProductFieldTypeEntity.ProductFactorType && (
        <Controller
          name={ `${variable.name}_${variable.insuredObjectId}` }
          control={ control }
          rules={ { required: variable.isRequired } }
          defaultValue={ getDefaultValue( factorValues, 'radio', variable.defaultValue ) }
          render={ ( props ) => (
            <Fragment>
              <Form.Label className={ variable.bfTooltip && 'tooltip-label' }>
                { variable.bfLabel ?
                  <div dangerouslySetInnerHTML={ { __html: `${variable.bfLabel}` } }></div>
                  : variable.label }
                { variable.bfTooltip && (
                  <a
                    id={ `tooltip-${variable.name}-${variable.insuredObjectId}` }
                    href={ blankLink }
                    onClick={ ( e ) => {
                      e.preventDefault();
                      onShowTooltip( true, variable.bfTooltip! );
                    } }
                  >
                    { t( 'bookingFunnel.tooltipHelp' ) }
                  </a>
                ) }
              </Form.Label>
              { variable.bfDescription && (
                <span className="gm-info f-12 mt-1">
                  { variable.bfDescription }
                </span>
              ) }
              <FormGroup className={ `custom-btn-radio mb-2 rdb-${props.name}` +
              `${factorValues.length === 3 ? ' btn-col-3' : ' btn-col-2'}`
              }
              >
                { factorValues.map( ( f, idx ) => {
                  return (
                    <Fragment key={ idx }>
                      <FormControl
                        { ...props }
                        className="form-check-input"
                        type="radio"
                        id={ `option-${ props.name + '-' + f.key}` }
                        value={ String( f.key ) }
                        checked={ props.value === String( f.key ) }
                        onChange={ ( e ) => handleChange( e, props.name ) }
                        onBlur={ props.onBlur }
                      />
                      <Form.Label
                        className="btn btn-border-radio"
                        htmlFor={ `option-${ props.name + '-' + f.key}` }
                      >
                        { f.name }
                      </Form.Label>
                    </Fragment>
                  );
                } ) }
                <Form.Control
                  id={ `error-messages-${props.name}` }
                  type="hidden"
                  isInvalid={ errors[props.name] !== undefined }
                />
                <FormControl.Feedback type="invalid">
                  { t( 'base:forms.messages.fieldRequired', {
                    fieldLabel: variable.label,
                  } ) }
                </FormControl.Feedback>
              </FormGroup>
            </Fragment>
          ) }
        />
      ) }
      { showTooltip && bfTooltip && (
        <TooltipPopup
          tooltipInfo={ bfTooltip }
          onClose={ () => onShowTooltip( false, '' ) }
        />
      ) }
    </Fragment>
  );
};
